'use client';

import { SyntheticEvent, useMemo, useState } from 'react';
import { Center, HStack, Img } from '@chakra-ui/react';
import ReactCrop from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import { captureException } from 'utils/helpers';
import { Button } from '../Button';
import { useCropImage } from './useCropImage';
import { centerAspectCrop, getBase64FromCrop } from './lib';

interface Props {
  src?: string;
  ratio: number;
  min: ImageDimensions;
  onSubmit?(base64: string): Promise<void> | void;
  onClose(): void;
  content: { title: string; cancelButton: string; confirmButton: string };
  /**
   * @default true
   * */
  convertToJpg?: boolean;
}

export type CropImageProps = Props;

export const CropImage = (props: Props) => {
  const {
    src,
    ratio,
    min,
    onClose,
    onSubmit,
    content,
    convertToJpg = true,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const { crop, onCropChange, completeCrop, onCropComplete, imgRef } =
    useCropImage({ ratio });

  const { cancelButton, confirmButton } = content;

  const { minWidth, minHeight } = useMemo(() => {
    if (!imgRef.current) return { minWidth: undefined, minHeight: undefined };

    const {
      width: imgWidth,
      height: imgHeight,
      naturalWidth: imgNaturalWidth,
      naturalHeight: imgNaturalHeight,
    } = imgRef.current;

    const percentWidth = (min.width / imgNaturalWidth) * 100;
    const percentHeight = (min.height / imgNaturalHeight) * 100;

    const minWidthCalc = Math.ceil((imgWidth * percentWidth) / 100);
    const minHeightCalc = Math.ceil((imgHeight * percentHeight) / 100);

    return {
      minWidth: minWidthCalc > imgWidth ? imgWidth : minWidthCalc,
      minHeight: minHeightCalc > imgHeight ? imgHeight : minHeightCalc,
    };
  }, [imgRef.current, min, src]);

  const onImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = event.currentTarget;

    const initialCrop = centerAspectCrop({
      imageWidth: width,
      imageHeight: height,
      cropWidth: height * ratio,
      cropHeight: width / ratio,
      ratio,
    });

    onCropChange(initialCrop);
    onCropComplete(initialCrop);
  };

  const onSave = async () => {
    try {
      if (!imgRef.current) return;

      setIsLoading(true);

      const base64 = await getBase64FromCrop({
        image: imgRef.current,
        crop: completeCrop,
        fileType: convertToJpg ? 'image/jpeg' : 'image/png',
      });

      await onSubmit?.(base64);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);

      captureException({
        error,
        tags: {
          component: 'Crop Image Modal',
          method: 'onSave',
        },
      });
    }
  };

  return (
    <Center flexDir="column" pb="1rem">
      <ReactCrop
        crop={crop}
        onChange={onCropChange}
        onComplete={onCropComplete}
        aspect={ratio}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={imgRef.current?.width}
        maxHeight={imgRef.current?.height}
        disabled={isLoading}
        style={{ boxShadow: '0 0 1rem #0000003d' }}
      >
        <Center h="full" w="full">
          <Img
            src={src}
            ref={imgRef}
            onLoad={onImageLoad}
            alt="Crop"
            maxH="40rem"
            objectFit="scale-down"
          />
        </Center>
      </ReactCrop>

      <HStack
        mt="1.5rem"
        w="full"
        spacing="2rem"
        justify={['space-between', 'space-between', 'flex-end']}
      >
        <Button
          onClick={onClose}
          isDisabled={isLoading}
          w={['full', 'full', 'auto']}
          px="4.5rem"
          variant="network"
        >
          {cancelButton}
        </Button>

        <Button
          onClick={onSave}
          isLoading={isLoading}
          px="4.5rem"
          w={['full', 'full', 'auto']}
          variant="network"
        >
          {confirmButton}
        </Button>
      </HStack>
    </Center>
  );
};
